import React, { useCallback, useEffect, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";

/**
 * Accessible before/after comparison with pointer drag + keyboard control.
 * The "after" image is the base layer, the "before" image is clipped.
 */
const BeforeAfter = ({ before, after, beforeAlt, afterAlt, className = "" }) => {
  const [pos, setPos] = useState(52);
  const [dragging, setDragging] = useState(false);
  const ref = useRef(null);

  const update = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const next = ((clientX - r.left) / r.width) * 100;
    setPos(Math.min(98, Math.max(2, next)));
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => update(e.touches ? e.touches[0].clientX : e.clientX);
    const up = () => setDragging(false);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [dragging, update]);

  const onKey = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(2, p - 4));
    if (e.key === "ArrowRight") setPos((p) => Math.min(98, p + 4));
    if (e.key === "Home") setPos(2);
    if (e.key === "End") setPos(98);
  };

  return (
    <div
      ref={ref}
      data-testid="before-after"
      className={`group relative aspect-[16/10] w-full select-none overflow-hidden rounded-[22px] bg-[#062B52] lg:aspect-[2.35/1] ${className}`}
      onPointerDown={(e) => {
        setDragging(true);
        update(e.clientX);
      }}
      style={{ cursor: dragging ? "grabbing" : "ew-resize", touchAction: "pan-y" }}
    >
      <img
        src={after}
        alt={afterAlt}
        loading="lazy"
        decoding="async"
        draggable="false"
        className="absolute inset-0 h-full w-full object-cover object-top"
      />
      <img
        src={before}
        alt={beforeAlt}
        loading="lazy"
        decoding="async"
        draggable="false"
        className="absolute inset-0 h-full w-full object-cover object-top"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      />

      {/* labels */}
      <span className="pointer-events-none absolute left-4 top-4 rounded-full bg-[#062B52]/85 px-3 py-1.5 font-mono-label text-[9.5px] text-white backdrop-blur-sm">
        Vorher
      </span>
      <span className="pointer-events-none absolute right-4 top-4 rounded-full bg-[#FF6B00] px-3 py-1.5 font-mono-label text-[9.5px] text-white">
        Nachher
      </span>

      {/* divider + handle */}
      <div
        className="pointer-events-none absolute inset-y-0 w-[2px] bg-white/90 shadow-[0_0_18px_rgba(6,43,82,0.5)]"
        style={{ left: `${pos}%`, transform: "translateX(-1px)" }}
      />
      <button
        type="button"
        role="slider"
        aria-label="Vorher-Nachher-Vergleich verschieben"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos)}
        onKeyDown={onKey}
        onPointerDown={(e) => {
          e.stopPropagation();
          setDragging(true);
        }}
        data-testid="before-after-handle"
        className="absolute top-1/2 z-10 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#062B52] shadow-[0_10px_30px_-8px_rgba(6,43,82,0.65)] transition-transform duration-300 hover:scale-105 focus-visible:scale-105"
        style={{ left: `${pos}%` }}
      >
        <MoveHorizontal className="h-5 w-5" />
      </button>
    </div>
  );
};

export default BeforeAfter;
